import { createSlice } from '@reduxjs/toolkit';
import { LineItemSourceType } from 'components/organisms/invoices/credit-notes/enums';
export var APIStatus = /*#__PURE__*/ function(APIStatus) {
    APIStatus["Idle"] = "idle";
    APIStatus["Loading"] = "loading";
    APIStatus["Success"] = "success";
    APIStatus["Error"] = "error";
    return APIStatus;
}({});
/** True only for finite numbers (0 included). Rejects undefined, null, NaN, non-numeric. */ export function isValidBudgetId(budgetId) {
    return typeof budgetId === 'number' && Number.isFinite(budgetId);
}
/** Composite key: projectId-budgetId when multi-budget, else projectId. Backward compatible. */ export function getLineItemKey(projectId, budgetId) {
    return isValidBudgetId(budgetId) ? `${projectId}-${budgetId}` : String(projectId);
}
const INITIAL_DATA = {
    creditNoteDetails: {},
    projects: [],
    lineItems: {},
    notes: undefined,
    attachments: [],
    currency: undefined,
    creditNoteSourceMappingIdsOrder: [],
    projectMappingIdsCanonicalOrder: [],
    creditNoteBudgetIdsOrderByProject: {},
    tax: 0,
    taxCodeRef: {
        taxCodeId: 0,
        taxCodeName: '',
        taxCodeRate: 0,
        taxComponents: []
    },
    fields: {},
    availableCredit: undefined,
    originalAmountByProject: {},
    originalLineItemsByProject: {},
    maxTax: 0,
    invoiceTaxRate: undefined,
    invoiceTax: undefined,
    hideBudgetBreakdown: false,
    selectCreditNoteProjectsFilter: undefined
};
const EMPTY_OBJECT = {};
const emptySlot = ()=>({
        lineItemOrder: [],
        lineDetails: {},
        originalLineDetails: {}
    });
function deselectProjectAndBudgets(projects, projectId) {
    return projects.map((p)=>{
        var _p_financialsBudgetMap;
        return p.projectId === projectId ? {
            ...p,
            selected: false,
            financialsBudgetMap: Object.fromEntries(Object.entries((_p_financialsBudgetMap = p.financialsBudgetMap) !== null && _p_financialsBudgetMap !== void 0 ? _p_financialsBudgetMap : {}).map(([k, b])=>[
                    k,
                    {
                        ...b,
                        selected: false
                    }
                ]))
        } : p;
    });
}
function removeProjectSlots(lineItems, projectId) {
    const updated = {
        ...lineItems
    };
    const prefix = String(projectId);
    Object.keys(updated).forEach((k)=>{
        if (k === prefix || k.startsWith(`${prefix}-`)) {
            delete updated[k];
        }
    });
    return updated;
}
function restoreSlotToOriginal(slot) {
    const originalLineDetails = slot.originalLineDetails || {};
    const resetLineDetails = {
        ...slot.lineDetails
    };
    Object.keys(originalLineDetails).forEach((lineId)=>{
        if (resetLineDetails[lineId]) {
            const { creditNoteLineItemId, ...originalWithoutTempId } = originalLineDetails[lineId];
            resetLineDetails[lineId] = {
                ...resetLineDetails[lineId],
                ...originalWithoutTempId
            };
        }
    });
    return {
        ...slot,
        lineDetails: resetLineDetails,
        lineItemOrder: slot.lineItemOrder || [],
        originalLineDetails
    };
}
export const creditNoteSlice = createSlice({
    initialState: INITIAL_DATA,
    name: 'creditNote',
    reducers: {
        setCreditNoteState: (state, { payload })=>{
            var _ref, _ref1;
            var _next_projectMappingIdsCanonicalOrder, _next_creditNoteSourceMappingIdsOrder;
            const next = {
                ...state,
                ...payload
            };
            // Seed the canonical order once on first load so re-added projects
            // can be inserted back at their original position.
            if (((_ref = (_next_projectMappingIdsCanonicalOrder = next.projectMappingIdsCanonicalOrder) === null || _next_projectMappingIdsCanonicalOrder === void 0 ? void 0 : _next_projectMappingIdsCanonicalOrder.length) !== null && _ref !== void 0 ? _ref : 0) === 0 && ((_ref1 = (_next_creditNoteSourceMappingIdsOrder = next.creditNoteSourceMappingIdsOrder) === null || _next_creditNoteSourceMappingIdsOrder === void 0 ? void 0 : _next_creditNoteSourceMappingIdsOrder.length) !== null && _ref1 !== void 0 ? _ref1 : 0) > 0) {
                next.projectMappingIdsCanonicalOrder = [
                    ...next.creditNoteSourceMappingIdsOrder
                ];
            }
            return next;
        },
        setAllProjects: (state, { payload })=>{
            return {
                ...state,
                projects: payload.projects
            };
        },
        addProjectAndCurrency: (state, { payload })=>{
            const project = state.projects.find((x)=>x.projectId === payload.projectId);
            if (!project) {
                return state;
            }
            const updatedProjects = state.projects.map((x)=>x.projectId === payload.projectId ? {
                    ...x,
                    selected: true
                } : x);
            const key = getLineItemKey(payload.projectId, payload.budgetId);
            return {
                ...state,
                projects: updatedProjects,
                currency: payload.currency,
                creditNoteSourceMappingIdsOrder: [
                    ...state.creditNoteSourceMappingIdsOrder,
                    project === null || project === void 0 ? void 0 : project.creditNoteToSourceMappingId
                ],
                lineItems: {
                    ...state.lineItems,
                    [key]: emptySlot()
                }
            };
        },
        setCreditNoteBasicDetails: (state, { payload })=>{
            return {
                ...state,
                creditNoteDetails: {
                    ...state.creditNoteDetails,
                    ...payload.creditNoteDetails
                },
                fields: {
                    ...state.fields,
                    ...payload.fields
                }
            };
        },
        setLineItems: (state, { payload: { data } })=>{
            const updatedData = Object.keys(data).reduce((acc, curr)=>{
                var _ref, _data_curr_lineItemOrder;
                var _state_lineItems_curr, _state_lineItems_curr1;
                // so that we dont remove the added line items when we go back to the line details screens
                const manuallyAddedItems = Object.keys((_ref = (_state_lineItems_curr = state.lineItems[curr]) === null || _state_lineItems_curr === void 0 ? void 0 : _state_lineItems_curr.lineDetails) !== null && _ref !== void 0 ? _ref : {}).reduce((a, x)=>{
                    var _state_lineItems_curr;
                    if ((_state_lineItems_curr = state.lineItems[curr]) === null || _state_lineItems_curr === void 0 ? void 0 : _state_lineItems_curr.lineDetails[x].isManuallyAdded) {
                        var _state_lineItems_curr1;
                        a.lineDetails = {
                            ...a.lineDetails,
                            [x]: (_state_lineItems_curr1 = state.lineItems[curr]) === null || _state_lineItems_curr1 === void 0 ? void 0 : _state_lineItems_curr1.lineDetails[x]
                        };
                        a.lineItemOrder = [
                            ...a.lineItemOrder,
                            Number(x)
                        ];
                    }
                    return a;
                }, {
                    lineDetails: {},
                    lineItemOrder: []
                });
                acc[curr] = {
                    lineItemOrder: [
                        ...(_data_curr_lineItemOrder = data[curr].lineItemOrder) !== null && _data_curr_lineItemOrder !== void 0 ? _data_curr_lineItemOrder : [],
                        ...manuallyAddedItems.lineItemOrder
                    ],
                    lineDetails: {
                        ...data[curr].lineDetails,
                        ...manuallyAddedItems.lineDetails
                    },
                    originalLineDetails: {
                        ...((_state_lineItems_curr1 = state.lineItems[curr]) === null || _state_lineItems_curr1 === void 0 ? void 0 : _state_lineItems_curr1.originalLineDetails) || {},
                        ...data[curr].originalLineDetails || {}
                    }
                };
                return acc;
            }, {});
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    ...updatedData
                }
            };
        },
        setSourceMappingOrder: (state, { payload: { data, replace } })=>{
            if (replace) {
                return {
                    ...state,
                    creditNoteSourceMappingIdsOrder: data
                };
            }
            // done so that order if set manually doesn't change when we add or remove new projects
            const excludingRemovedIds = state.creditNoteSourceMappingIdsOrder.filter((x)=>data.includes(x));
            // this will add all data to existing one, but making it a set will keep the order of insertion and duplicates that are later added will get removed
            const allSourceIds = [
                ...excludingRemovedIds,
                ...data
            ];
            const uniqSourceIds = [
                ...new Set(allSourceIds)
            ];
            return {
                ...state,
                creditNoteSourceMappingIdsOrder: uniqSourceIds
            };
        },
        deleteLineItem: (state, { payload: { projectId, lineId, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const updatedLineOrder = state.lineItems[key].lineItemOrder.filter((x)=>x !== lineId);
            const currentProjectLineItemsState = {
                lineDetails: {
                    ...state.lineItems[key].lineDetails
                },
                lineItemOrder: updatedLineOrder,
                originalLineDetails: {
                    ...state.lineItems[key].originalLineDetails || {}
                }
            };
            delete currentProjectLineItemsState.lineDetails[lineId];
            delete currentProjectLineItemsState.originalLineDetails[lineId];
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: currentProjectLineItemsState
                }
            };
        },
        createLineItem: (state, { payload: { projectId, lineId, taxCodeRef, lineItemSource, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineItemOrder: state.lineItems[key].lineItemOrder.concat(lineId),
                        lineDetails: {
                            ...state.lineItems[key].lineDetails,
                            [lineId]: {
                                creditNoteLineItemId: lineId,
                                invoicedEntity: EMPTY_OBJECT,
                                description: '',
                                quantity: 0,
                                unitPrice: 0,
                                amount: 0,
                                taxCodeRef,
                                projectId,
                                isNew: true,
                                isManuallyAdded: true,
                                price: 0,
                                lineItemSource
                            }
                        },
                        originalLineDetails: {
                            ...state.lineItems[key].originalLineDetails
                        }
                    }
                }
            };
        },
        insertLineItems: (state, { payload: { projectId, lineItems, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const currentProjectLineItems = state.lineItems[key] || {
                lineItemOrder: [],
                lineDetails: {},
                originalLineDetails: {}
            };
            const newLineDetails = lineItems.reduce((acc, lineItem)=>{
                acc[lineItem.creditNoteLineItemId] = lineItem;
                return acc;
            }, {});
            const newOriginalLineDetails = lineItems.reduce((acc, lineItem)=>{
                acc[lineItem.creditNoteLineItemId] = {
                    ...lineItem
                };
                return acc;
            }, {});
            const newLineItemIds = lineItems.map((item)=>item.creditNoteLineItemId);
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineItemOrder: [
                            ...currentProjectLineItems.lineItemOrder,
                            ...newLineItemIds
                        ],
                        lineDetails: {
                            ...currentProjectLineItems.lineDetails,
                            ...newLineDetails
                        },
                        originalLineDetails: {
                            ...currentProjectLineItems.originalLineDetails || {},
                            ...newOriginalLineDetails
                        }
                    }
                }
            };
        },
        /** Budget-level reset: restores a single project/budget slot to its original snapshot. */ resetProjectBudgetItems: (state, { payload: { projectId, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const projectLineItems = state.lineItems[key];
            if (!projectLineItems) {
                return state;
            }
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: restoreSlotToOriginal(projectLineItems)
                }
            };
        },
        /**
     * Project-level reset: restores every slot belonging to the project — both
     * the project-only key (`<projectId>`) and all budget keys
     * (`<projectId>-<budgetId>`) — back to their original snapshots.
     */ resetProjectItems: (state, { payload: { projectId } })=>{
            const projectKey = String(projectId);
            const budgetKeyPrefix = `${projectId}-`;
            const matchingKeys = Object.keys(state.lineItems).filter((key)=>key === projectKey || key.startsWith(budgetKeyPrefix));
            if (matchingKeys.length === 0) {
                return state;
            }
            const updatedLineItems = {
                ...state.lineItems
            };
            matchingKeys.forEach((key)=>{
                updatedLineItems[key] = restoreSlotToOriginal(updatedLineItems[key]);
            });
            return {
                ...state,
                lineItems: updatedLineItems
            };
        },
        resetImportedItems: (state)=>{
            const updatedLineItems = Object.keys(state.lineItems).reduce((acc, projectId)=>{
                const projectLineItems = state.lineItems[projectId];
                const originalLineDetails = projectLineItems.originalLineDetails || {};
                // Replace all edited items with their original unedited versions
                const resetLineDetails = {
                    ...projectLineItems.lineDetails
                };
                Object.keys(originalLineDetails).forEach((lineId)=>{
                    if (resetLineDetails[lineId]) {
                        // Restore the original unedited version
                        // Exclude creditNoteLineItemId as it's a temp id that would replace the correct id
                        const { creditNoteLineItemId, ...originalWithoutTempId } = originalLineDetails[lineId];
                        resetLineDetails[lineId] = {
                            ...resetLineDetails[lineId],
                            ...originalWithoutTempId
                        };
                    }
                });
                return {
                    ...acc,
                    [projectId]: {
                        ...projectLineItems,
                        lineDetails: resetLineDetails,
                        lineItemOrder: projectLineItems.lineItemOrder || [],
                        originalLineDetails
                    }
                };
            }, {});
            return {
                ...state,
                lineItems: updatedLineItems
            };
        },
        updateLineItem: (state, { payload: { projectId, lineId, updates, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineItemOrder: state.lineItems[key].lineItemOrder,
                        lineDetails: {
                            ...state.lineItems[key].lineDetails,
                            [lineId]: {
                                ...state.lineItems[key].lineDetails[lineId],
                                ...updates
                            }
                        },
                        originalLineDetails: state.lineItems[key].originalLineDetails || {}
                    }
                }
            };
        },
        updateLineItemOrder: (state, { payload: { projectId, newLineItemOrder, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineDetails: state.lineItems[key].lineDetails,
                        lineItemOrder: newLineItemOrder,
                        originalLineDetails: state.lineItems[key].originalLineDetails || {}
                    }
                }
            };
        },
        setFields: (state, { payload })=>{
            return {
                ...state,
                fields: {
                    ...state.fields,
                    ...payload
                }
            };
        },
        updateAllLineItemsTaxAndFinalAmount: (state, { payload: { updatedTax, projectId, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const updatedLineItems = {};
            const projectLineItems = state.lineItems[key];
            const updatedLineDetails = {};
            Object.keys(projectLineItems.lineDetails).forEach((lineItemId)=>{
                var _ref;
                const lineItem = projectLineItems.lineDetails[lineItemId];
                const effectiveTax = updatedTax;
                const amount = (1 + ((_ref = effectiveTax === null || effectiveTax === void 0 ? void 0 : effectiveTax.taxCodeRate) !== null && _ref !== void 0 ? _ref : 0) / 100) * lineItem.amount;
                if (lineItem.lineItemSource === LineItemSourceType.Custom) {
                    updatedLineDetails[lineItemId] = {
                        ...lineItem,
                        taxCodeRef: effectiveTax,
                        amount
                    };
                } else {
                    updatedLineDetails[lineItemId] = lineItem;
                }
            });
            updatedLineItems[key] = {
                ...projectLineItems,
                lineDetails: updatedLineDetails,
                originalLineDetails: projectLineItems.originalLineDetails || {}
            };
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    ...updatedLineItems
                },
                taxCodeRef: updatedTax
            };
        },
        removeProject: (state, { payload: { projectId } })=>{
            const projectToRemove = state.projects.find((p)=>p.projectId === projectId);
            return {
                ...state,
                projects: deselectProjectAndBudgets(state.projects, projectId),
                lineItems: removeProjectSlots(state.lineItems, projectId),
                creditNoteSourceMappingIdsOrder: state.creditNoteSourceMappingIdsOrder.filter((x)=>x !== (projectToRemove === null || projectToRemove === void 0 ? void 0 : projectToRemove.creditNoteToSourceMappingId))
            };
        },
        removeBudget: (state, { payload: { projectId, budgetId } })=>{
            var _ref;
            const project = state.projects.find((p)=>p.projectId === projectId);
            const isLastBudget = Object.values((_ref = project === null || project === void 0 ? void 0 : project.financialsBudgetMap) !== null && _ref !== void 0 ? _ref : {}).filter((b)=>b.projectFinancialsBudgetId !== budgetId && b.selected).length === 0;
            if (isLastBudget) {
                return {
                    ...state,
                    projects: deselectProjectAndBudgets(state.projects, projectId),
                    lineItems: removeProjectSlots(state.lineItems, projectId),
                    creditNoteSourceMappingIdsOrder: state.creditNoteSourceMappingIdsOrder.filter((x)=>x !== (project === null || project === void 0 ? void 0 : project.creditNoteToSourceMappingId))
                };
            }
            const lineItems = {
                ...state.lineItems
            };
            delete lineItems[getLineItemKey(projectId, budgetId)];
            return {
                ...state,
                projects: state.projects.map((p)=>{
                    var _p_financialsBudgetMap;
                    return p.projectId === projectId ? {
                        ...p,
                        financialsBudgetMap: {
                            ...p.financialsBudgetMap,
                            [budgetId]: {
                                ...(_p_financialsBudgetMap = p.financialsBudgetMap) === null || _p_financialsBudgetMap === void 0 ? void 0 : _p_financialsBudgetMap[budgetId],
                                selected: false
                            }
                        }
                    } : p;
                }),
                lineItems
            };
        },
        addBudget: (state, { payload: { projectId, budgetId } })=>{
            const updatedProjects = state.projects.map((project)=>{
                var _project_financialsBudgetMap;
                return project.projectId === projectId ? {
                    ...project,
                    financialsBudgetMap: {
                        ...project.financialsBudgetMap,
                        [budgetId]: {
                            ...(_project_financialsBudgetMap = project.financialsBudgetMap) === null || _project_financialsBudgetMap === void 0 ? void 0 : _project_financialsBudgetMap[budgetId],
                            selected: true
                        }
                    }
                } : project;
            });
            return {
                ...state,
                projects: updatedProjects
            };
        },
        addProjectsAndBudgets: (state, { payload: { selections, currency } })=>{
            if (selections.length === 0) {
                return state;
            }
            const selectedBudgetsByProject = selections.reduce((acc, selection)=>{
                if (!acc[selection.projectId]) {
                    acc[selection.projectId] = new Set();
                }
                if (isValidBudgetId(selection.budgetId)) {
                    acc[selection.projectId].add(selection.budgetId);
                }
                return acc;
            }, {});
            const projects = state.projects.map((project)=>{
                const budgetIds = selectedBudgetsByProject[project.projectId];
                if (!budgetIds) {
                    return project;
                }
                const updatedProject = {
                    ...project,
                    selected: true
                };
                if (project.financialsBudgetMap && budgetIds.size > 0) {
                    updatedProject.financialsBudgetMap = Object.fromEntries(Object.entries(project.financialsBudgetMap).map(([k, budget])=>[
                            k,
                            budgetIds.has(budget.projectFinancialsBudgetId) ? {
                                ...budget,
                                selected: selectedBudgetsByProject[project.projectId].has(budget.projectFinancialsBudgetId)
                            } : budget
                        ]));
                }
                return updatedProject;
            });
            const lineItems = {
                ...state.lineItems
            };
            const creditNoteSourceMappingIdsOrder = [
                ...state.creditNoteSourceMappingIdsOrder
            ];
            const projectMappingIdsCanonicalOrder = [
                ...state.projectMappingIdsCanonicalOrder
            ];
            const creditNoteBudgetIdsOrderByProject = {
                ...state.creditNoteBudgetIdsOrderByProject
            };
            selections.forEach(({ projectId, budgetId })=>{
                const project = projects.find((p)=>p.projectId === projectId);
                if (project && !creditNoteSourceMappingIdsOrder.includes(project.creditNoteToSourceMappingId)) {
                    // Insert at the position dictated by the canonical order so that a
                    // re-added project lands where it was originally, not at the bottom.
                    const mappingId = project.creditNoteToSourceMappingId;
                    const canonicalIdx = projectMappingIdsCanonicalOrder.indexOf(mappingId);
                    if (canonicalIdx === -1) {
                        // Genuinely new project — append and record in canonical order.
                        creditNoteSourceMappingIdsOrder.push(mappingId);
                        projectMappingIdsCanonicalOrder.push(mappingId);
                    } else {
                        // Previously seen — find the insertion point by locating the last
                        // active predecessor from the canonical sequence.
                        const predecessors = new Set(projectMappingIdsCanonicalOrder.slice(0, canonicalIdx));
                        let insertIdx = 0;
                        for(let i = creditNoteSourceMappingIdsOrder.length - 1; i >= 0; i -= 1){
                            if (predecessors.has(creditNoteSourceMappingIdsOrder[i])) {
                                insertIdx = i + 1;
                                break;
                            }
                        }
                        creditNoteSourceMappingIdsOrder.splice(insertIdx, 0, mappingId);
                    }
                }
                const key = getLineItemKey(projectId, budgetId);
                if (!lineItems[key]) {
                    lineItems[key] = emptySlot();
                }
                if (isValidBudgetId(budgetId)) {
                    var _creditNoteBudgetIdsOrderByProject_projectId;
                    const existingOrder = (_creditNoteBudgetIdsOrderByProject_projectId = creditNoteBudgetIdsOrderByProject[projectId]) !== null && _creditNoteBudgetIdsOrderByProject_projectId !== void 0 ? _creditNoteBudgetIdsOrderByProject_projectId : [];
                    if (!existingOrder.includes(budgetId)) {
                        creditNoteBudgetIdsOrderByProject[projectId] = [
                            ...existingOrder,
                            budgetId
                        ];
                    }
                }
            });
            return {
                ...state,
                projects,
                lineItems,
                creditNoteSourceMappingIdsOrder,
                projectMappingIdsCanonicalOrder,
                creditNoteBudgetIdsOrderByProject,
                currency: currency !== null && currency !== void 0 ? currency : state.currency
            };
        },
        setBudgetSelection: (state, { payload: { projectId, budgetId, selected } })=>{
            var _state_creditNoteBudgetIdsOrderByProject_projectId;
            const projects = state.projects.map((p)=>{
                if (p.projectId !== projectId || !p.financialsBudgetMap) {
                    return p;
                }
                return {
                    ...p,
                    financialsBudgetMap: Object.fromEntries(Object.entries(p.financialsBudgetMap).map(([k, b])=>[
                            k,
                            b.projectFinancialsBudgetId === budgetId ? {
                                ...b,
                                selected
                            } : b
                        ]))
                };
            });
            const key = getLineItemKey(projectId, budgetId);
            const lineItems = {
                ...state.lineItems
            };
            if (selected) {
                if (!lineItems[key]) {
                    lineItems[key] = emptySlot();
                }
            } else {
                delete lineItems[key];
            }
            const existingOrder = (_state_creditNoteBudgetIdsOrderByProject_projectId = state.creditNoteBudgetIdsOrderByProject[projectId]) !== null && _state_creditNoteBudgetIdsOrderByProject_projectId !== void 0 ? _state_creditNoteBudgetIdsOrderByProject_projectId : [];
            const nextOrder = selected ? existingOrder.includes(budgetId) ? existingOrder : [
                ...existingOrder,
                budgetId
            ] : existingOrder.filter((id)=>id !== budgetId);
            return {
                ...state,
                projects,
                lineItems,
                creditNoteBudgetIdsOrderByProject: {
                    ...state.creditNoteBudgetIdsOrderByProject,
                    [projectId]: nextOrder
                }
            };
        },
        setBudgetOrderByProject: (state, { payload: { data, replace } })=>{
            if (replace) {
                return {
                    ...state,
                    creditNoteBudgetIdsOrderByProject: data
                };
            }
            return {
                ...state,
                creditNoteBudgetIdsOrderByProject: {
                    ...state.creditNoteBudgetIdsOrderByProject,
                    ...data
                }
            };
        },
        setAvailableCredit: (state, { payload })=>{
            return {
                ...state,
                availableCredit: payload
            };
        },
        setSelectCreditNoteProjectsFilter: (state, { payload })=>{
            return {
                ...state,
                selectCreditNoteProjectsFilter: payload
            };
        },
        resetAllData: ()=>{
            return INITIAL_DATA;
        }
    }
});
